<script setup>
import { useFloating, offset, flip, shift, autoUpdate } from '@floating-ui/vue'
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

import { usePopover, useClickOutside, isElementVisible } from '@/composables/usePopover'

const props = defineProps({
  closeOnClick: {
    type: Boolean,
    default: true,
  },
  placement: {
    type: String,
    default: 'bottom-end',
  },
  icon: {
    type: String,
    default: '3-dots-more',
  },
})

const { isOpen, toggle, close } = usePopover()
const buttonRef = ref(null)
const contentRef = ref(null)
const isMobile = ref(false)

// Detectar tamanho da tela
const checkMobile = () => {
  isMobile.value = window.innerWidth < 640 // sm breakpoint
}

// Setup floating (apenas para desktop)
const { x, y, strategy, update } = useFloating(buttonRef, contentRef, {
  placement: props.placement,
  middleware: [offset(2), flip(), shift()],
  whileElementsMounted: autoUpdate,
})

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile)
})

// Watch for open state to trigger update (apenas em desktop)
watch(isOpen, async value => {
  if (value && !isMobile.value) {
    await nextTick()
    update()
  }
})

// Se o trigger ficou invisível após a troca de breakpoint (ex: ancestral com
// classe `sm:hidden`), o popover não tem onde ancorar — fechar é a UX correta.
// Quando o trigger continua visível, deixamos o Floating UI reposicionar.
watch(isMobile, () => {
  if (!isElementVisible(buttonRef.value)) close()
})

useClickOutside([buttonRef, contentRef], close)

const handleClick = () => {
  if (props.closeOnClick) {
    close()
  }
}
</script>

<template>
  <div class="relative">
    <div @click="toggle" ref="buttonRef" class="cursor-pointer">
      <slot name="dropdown-button">
        <ImoIcon :name="icon" class="rotate-90" :size="16" />
      </slot>
    </div>

    <Teleport to="body">
      <!-- Backdrop para mobile -->
      <Transition
        enter-active-class="transition-opacity duration-300 ease-out"
        leave-active-class="transition-opacity duration-200 ease-in"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen && isMobile"
          class="fixed inset-0 z-40 bg-gray-400/75 backdrop-blur-sm"
          @click="isOpen = false"
        />
      </Transition>

      <!-- Bottom Sheet (mobile) -->
      <Transition
        enter-active-class="transition-transform duration-300 ease-out"
        leave-active-class="transition-transform duration-200 ease-in"
        enter-from-class="translate-y-full"
        enter-to-class="translate-y-0"
        leave-from-class="translate-y-0"
        leave-to-class="translate-y-full"
      >
        <div
          v-if="isOpen && isMobile"
          ref="contentRef"
          @click="handleClick"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
          class="border-neutral-20 fixed right-0 bottom-0 left-0 z-50 rounded-t-3xl border bg-white px-4 py-6 shadow-lg"
        >
          <slot name="dropdown-content" />
        </div>
      </Transition>

      <!-- Dropdown (desktop) -->
      <Transition
        enter-active-class="transition-opacity duration-200 ease-out"
        leave-active-class="transition-opacity duration-150 ease-in"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <div
          v-if="isOpen && !isMobile"
          ref="contentRef"
          @click="handleClick"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
          :style="{
            position: strategy,
            top: `${y ?? 0}px`,
            left: `${x ?? 0}px`,
          }"
          class="border-neutral-20 z-50 rounded-lg border bg-white p-1 shadow-lg"
        >
          <slot name="dropdown-content" />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
